import { useCallback, useEffect, useState } from 'react';
import { t } from 'ttag';
import { useLocation } from 'react-router-dom';

import { SavedSearchSubscribe } from '@components/SavedSearchSubscribe';
import { notifyError, notifySuccess } from '@utils/notifications';
import { useCatalogRouteMatch } from '@utils/common';
import { useFilterData } from '@hook/productFilters';

import {
  useMarkSavedSearchProductsMutation,
  useRemoveSavedSearchSubscribeldMutation,
  useSavedSearchSubscribeldMutation,
} from './graphql.v4.generated';

type SavedSearchSubscribeldProps = {
  isSavedSearchTop?: boolean
  productId?: string | null;
};

export const SavedSearchSubscribeldContainer:
React.FC<SavedSearchSubscribeldProps> = ({
  isSavedSearchTop,
  productId,
}) => {
  const location = useLocation();
  const catalogRouteMatch = useCatalogRouteMatch();
  const { filters } = useFilterData();
  const searchId = Number(new URLSearchParams(location.search).get('saved_search_id'));

  const [isSaved, setIsSaved] = useState(!!searchId);

  const [savedSearchSubsId, setSavedSearchSubsId] = useState<number>();

  const [markSavedSearchAsViewed, { error }] = useMarkSavedSearchProductsMutation({
    context: { v4: true },
  });

  const [savedSearch] = useSavedSearchSubscribeldMutation({
    context: { v4: true },
  });

  const [removeSavedSearch] = useRemoveSavedSearchSubscribeldMutation({
    context: { v4: true },
  });

  useEffect(
    () => setIsSaved(!!searchId),
    [searchId, location],
  );

  const onSavedSearchSubsr = async () => {
    const rootCatalogSlug = catalogRouteMatch?.params.namespace || '';
    const subCatalogSlug = catalogRouteMatch?.params && 'slug' in catalogRouteMatch.params
      ? catalogRouteMatch?.params.slug
      : '';
    const params = new URLSearchParams(location.search);
    const searchText = params.get('search_text');
    const ukrainian = Boolean(params.get('ukrainian'));
    const isOnSale = Boolean(params.get('is_on_sale'));
    const freeShipping = Boolean(params.get('free_shipping'));
    const priceFrom = +params.getAll('price_from') === 0 ? null : +params.getAll('price_from');
    const priceTo = +params.getAll('price_to') === 0 ? null : +params.getAll('price_to');
    const cities = params.getAll('cities').map((city) => +city);
    const colors = params.getAll('colors').map((color) => +color);
    const conditions = params.getAll('conditions').map((condition) => +condition);
    const brands = params.getAll('brands').map((brand) => +brand);
    const sizes = params.getAll('sizes').map((size) => +size);
    const { characteristics } = filters;
    const resp = await savedSearch({
      variables: {
        catalogSlug: subCatalogSlug || rootCatalogSlug,
        priceFrom,
        priceTo,
        conditions,
        cities,
        brands,
        colors,
        sizes,
        characteristics,
        searchText,
        ukrainian,
        isOnSale,
        freeShipping,
      },
    });
    if (resp.data?.saveSearch.errors?.length) {
      notifyError(t`Неможливо підписатися на пошук`);
    } else {
      notifySuccess(t`Пошук збережено, ви отримаєте повідомлення, коли з'являться нові речі`);
      setIsSaved(true);
    }
    const savedSearchId = resp.data?.saveSearch.savedSearchId;
    if (savedSearchId) {
      setSavedSearchSubsId(savedSearchId);
      const url = new URL(window.location.href);
      const newParams = new URLSearchParams(url.search);
      newParams.set('saved_search_id', `${savedSearchId}`);
      window.history.pushState(null, '', `${url.pathname}?${newParams.toString()}`);
    }
  };
  const onRemoveSavedSearch = async () => {
    const resp = await removeSavedSearch({
      variables: { savedSearchId: savedSearchSubsId! || searchId },
    });

    if (resp.data?.removeSavedSearch.errors && resp.errors) {
      notifyError(t`Пошук неможливо видалити`);
    } else {
      notifySuccess(t`Пошук видалено`);
      setIsSaved(false);
      const url = new URL(window.location.href);
      url.searchParams.delete('saved_search_id');
      url.searchParams.delete('last_viewed_product_id');
      window.history.pushState(null, document.title, url.href);
    }
  };

  if (error) {
    throw new Error('mark is failed');
  }

  const markSavedSearchProducts = useCallback(
    (savedProductId: string, markSavedSearchId: number) => markSavedSearchAsViewed({
      variables: { savedSearchId: +markSavedSearchId, lastViewedProductId: +savedProductId },
    }), [markSavedSearchAsViewed],
  );

  useEffect(() => {
    if (productId && searchId) {
      markSavedSearchProducts(productId, searchId);
    }
  }, [productId, searchId, markSavedSearchProducts]);

  return (
    <>
      <SavedSearchSubscribe
        isSavedSearchTop={isSavedSearchTop}
        isSaved={isSaved}
        onSavedSearch={onSavedSearchSubsr}
        onRemoveSavedSearch={onRemoveSavedSearch}
      />
    </>

  );
};
